import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import { ActivityMediumLink } from '@/components/activities/ActivityMediumLink';
import {
  BaseActivity,
  BaseActivityArticleName,
  BaseActivityDetailedContent,
  BaseActivityText,
} from '@/components/activities/BaseActivity/BaseActivity';
import Stack from '@/design_system/Stack';
import IconAnalysisIssue from '@/icons/AnalysisIssue.svg';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useGetExchangeTypes } from '@/utils/exchangeTypes';

export const ArticleRequalifiedActivity = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityOfType<'article_requalified'>;
  displayArticleName: boolean;
}) => {
  const { iam } = useRequestContext();

  const userWorkshopIds = iam.scopesOnRequest
    .filter(({ scope }) => scope === 'workshop')
    .map(({ entityId }) => entityId);
  const hideQuote =
    iam.hasOnlyWorkshopScopeOnRequest && !userWorkshopIds.includes(activity.workshopId);
  const hasComment = !!activity.data.comment && activity.data.comment.trim() !== '';

  return (
    <BaseActivity
      createdAt={activity.createdAt}
      type="warning"
      icon={<IconAnalysisIssue />}
      message={
        <BaseActivityText>
          <ArticleRequalifiedMessage activity={activity} displayArticleName={displayArticleName} />
        </BaseActivityText>
      }
    >
      <Stack gap="0.5rem" style={{ flex: 1 }}>
        {activity.medium && !hideQuote && <ActivityMediumLink medium={activity.medium} />}
        {hasComment && (
          <BaseActivityDetailedContent>{activity.data.comment}</BaseActivityDetailedContent>
        )}
      </Stack>
    </BaseActivity>
  );
};

const ArticleRequalifiedMessage = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityOfType<'article_requalified'>;
  displayArticleName: boolean;
}) => {
  const { exchangeTypeOptions } = useGetExchangeTypes();
  const { t } = useLingui();

  const workshopName = activity.workshop.name;

  const creatorName = `${activity.creator.name}${!activity.data?.onBehalfOfWorkshop ? ` (${workshopName})` : ''}`;

  const exchangeType =
    exchangeTypeOptions.find((type) => type.value === activity.data?.exchange?.type)?.label ||
    t({
      id: 'service-choice.radio.exchange.label',
      message: 'Exchange',
    });

  const productName = activity.data?.exchange?.replacementProductName || '';

  const articleName = (
    <BaseActivityArticleName activity={activity} displayArticleName={displayArticleName} />
  );

  if (activity.data?.exchange) {
    return (
      <Trans id="activities.article_requalified.message.exchange">
        <strong>{creatorName}</strong> has created an exchange proposal of type {exchangeType} with
        product {productName}.
      </Trans>
    );
  }

  if (activity.data?.onBehalfOfWorkshop) {
    if (activity.data.skipRequalificationAndClientValidation) {
      return (
        <Trans id="activities.article_requalified.message.on-behalf.skipped-validation">
          <strong>{creatorName}</strong> submitted a new quote for the item {articleName} on behalf
          of {workshopName} and skipped the validation step
        </Trans>
      );
    }

    return (
      <Trans id="activities.article_requalified.message.on-behalf">
        <strong>{creatorName}</strong> submitted a new quote for the item {articleName} on behalf of{' '}
        {workshopName}
      </Trans>
    );
  }

  if (activity.data.skipRequalificationAndClientValidation) {
    return (
      <Trans id="activities.article_requalified.message.skipped-validation">
        <strong>{creatorName}</strong> submitted a new quote for the item {articleName} and skipped
        the validation step
      </Trans>
    );
  }

  return (
    <Trans id="activities.article_requalified.message">
      <strong>{creatorName}</strong> submitted a new quote for the item {articleName}
    </Trans>
  );
};
